<template>
    <div class="flex flex-col h-full gap-2 p-2">
        <local-grid ref="roleGridRef" :data-source="displayList" :meta="meta">
            <template #toolbarItems>
                <dx-item location="before">
                    <dx-drop-down-box
                        v-model:value="dropDownBoxValue"
                        :data-source="environmentState.appList"
                        :show-drop-down-button="true"
                        :opened="isDropDownBoxOpened"
                        :display-value-formatter="displayValueFormatter"
                        value-expr="kod_app"
                        display-expr="name_app"
                        placeholder="Выберите приложения..."
                        width="420"
                        drop-down-button-template="imageIcon"
                        @closed="onDropDownClose"
                        @opened="onDropDownOpened"
                    >
                        <template #imageIcon="{}">
                            <img alt="Filter" :src="'/infoenergo/assets/filter.svg'" />
                        </template>
                        <template #content>
                            <dx-form @field-data-changed="filterParamsChange">
                                <dx-simple-item
                                    v-for="app in apps"
                                    :key="app.kod_app"
                                    :data-field="app.name_app"
                                    :label="{ visible: false }"
                                    :editor-options="{ text: app.name_app, value: app.isChecked }"
                                    editor-type="dxCheckBox"
                                />
                            </dx-form>
                            <div class="custom-filter-form-button flex justify-end">
                                <ai-button
                                    text="Показать роли"
                                    :style="{ minWidth: '120px', height: '30px' }"
                                    @click="handleSubmit"
                                />
                            </div>
                        </template>
                    </dx-drop-down-box>
                </dx-item>

                <dx-item widget="dxButton" location="after" show-text="inMenu" locate-in-menu="auto">
                    <dx-button class="ai-button-base" hint="Обновить данные" @click="refreshGridData">
                        <ai-refresh-base />
                    </dx-button>
                </dx-item>

                <dx-item widget="dxButton" location="after" show-text="inMenu" locate-in-menu="auto">
                    <dx-button
                        class="ai-button-base"
                        hint="Создать роль"
                        :disabled="isDisableUiElement"
                        @click="showPopup"
                    >
                        <ai-add-base />
                    </dx-button>
                </dx-item>

                <dx-item widget="dxButton" location="after" show-text="inMenu" locate-in-menu="auto">
                    <dx-button
                        class="ai-button-base"
                        :disabled="isDisableUiElement || disabled"
                        @click="deleteRolesFromToolbar"
                    >
                        <ai-delete-base />
                    </dx-button>
                </dx-item>
            </template>
        </local-grid>
    </div>
    <component
        :is="rolePopupSwitcher"
        @restartWatching="startWatching"
        @refreshGridData="refreshGridData"
    />
    <component :is="areasPopupSwitcher" />
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { DxButton } from 'devextreme-vue';
import { DxItem } from 'devextreme-vue/data-grid';
import DxDropDownBox from 'devextreme-vue/drop-down-box';
import { DxForm, DxSimpleItem } from 'devextreme-vue/form';
import { custom } from 'devextreme/ui/dialog';
import LocalGrid from '@/components/local-grid.vue';
import RolePopupCard from '@/views/administration/roles/role-card-popup.vue';
import AreasPopup from '@/views/administration/roles/areas-popup.vue';
import { DxUiNotify } from '@/utils/dx-ui-notify.js';
import { dxUiConfirm } from '@/utils/dx-helper.js';
import { getCookie } from '@/utils/cookies.js';
import environmentStore from '@/store/enviroment-data.js';
import rolesStore from '@/store/administration/roles.js';
import roleLocalStore from '@/views/administration/roles/store.js';
import AiButton from '@/components/ai-button/ai-button.vue';

const { state: environmentState, getAppList, disableUiElement } = environmentStore;
const { rolesState, getRolesByApps, getRoleDependencies, getGroupList, deleteRole } = rolesStore;
const { state, clearRoleData, loadById, copyById, isNotSystemRole, MODE, UI_ELEMENTS_IDS } = roleLocalStore;

const currentApp = ref(+getCookie('app'));
const roleGridRef = ref();
const gridInstance = ref();
const disabled = ref(true);
const dropDownBoxValue = ref([currentApp.value]);
const apps = ref();
const isDropDownBoxOpened = ref(false);
const rolePopupSwitcher = computed(() => state.rolePopupVisible ? RolePopupCard : null);
const areasPopupSwitcher = computed(() => state.areaPopupVisible ? AreasPopup : null);
const isDisableUiElement = disableUiElement(UI_ELEMENTS_IDS.ROLES_GRID);

onMounted(async () => {
    await getRolesByApps([currentApp.value]);
    await getGroupList();
    await initApps();
});

onMounted(() => {
    gridInstance.value = roleGridRef.value?.gridInstance;
    gridInstance.value.on('contextMenuPreparing', onContextMenuPreparing);
    gridInstance.value.on('selectionChanged', onSelectionChanged);
    gridInstance.value.option('filterValue', environmentState.roleGridFiltersValue);
});

onUnmounted(() => {
    environmentState.roleGridFiltersValue = gridInstance.value.option('filterValue');
});

const initApps = async () => {
    await getAppList();
    apps.value = environmentState.appList?.map(app => ({
            ...app,
            isChecked: app.kod_app === currentApp.value,
        }),
    );
};

const displayList = computed(() => rolesState.rolesByAppsIds);

const handleSubmit = async () => {
    const selectedFilters = [];
    apps.value?.forEach(app => {
        if (app.isChecked) selectedFilters.push(app.kod_app);
    });
    dropDownBoxValue.value = selectedFilters;
    isDropDownBoxOpened.value = false;
    await getRolesByApps(selectedFilters);
};

const displayValueFormatter = value => {
    return value.length
        ? value.join(', ')
        : value;
};

const filterParamsChange = event => {
    apps.value?.forEach(app => {
        if (app.name_app === event.dataField) app.isChecked = event.value;
    });
};

const onDropDownClose = () => {
    isDropDownBoxOpened.value = false;
};

const onDropDownOpened = () => {
    isDropDownBoxOpened.value = true;
};

const onSelectionChanged = event => {
    disabled.value = !event.selectedRowKeys.length;
};

const startWatching = () => {
    state.requestBodyHasNotChanged = true;
    if (!isNotSystemRole()) return;
    const originalAreas = structuredClone(state.areas);
    const originalCode = state.formData.code;
    const originalName = state.formData.name;
    const originalDescription = state.formData.description;
    state.stopWatching = watch(
        () => [state.areas, state.formData.code, state.formData.name, state.formData.description],
        () => {
            state.requestBodyHasNotChanged =
                JSON.stringify(originalAreas) === JSON.stringify(state.areas) &&
                originalCode === state.formData.code &&
                originalName === state.formData.name &&
                originalDescription === state.formData.description;
        },
        { deep: true },
    );
};

const showPopup = () => {
    state.mode = MODE.CREATE;
    clearRoleData();
    state.rolePopupVisible = true;
    startWatching();
    state.appId = currentApp.value;
};

const refreshGridData = async () => {
    await getRolesByApps(dropDownBoxValue.value);
};

const deleteRolesFromToolbar = () => {
    const selectedRows = gridInstance.value.getSelectedRowsData();
    deleteRoles(selectedRows);
};

const onContextMenuPreparing = event => {
    if (!event.row) return;
    if (event.target === 'content') {
        if (!event.items) event.items = [];
        const isSystemRole = event.row.data.code === '-1';
        const roleHasMoreThanOneApps = event.row.data.app_ids.length > 1;
        event.items.push(
            {
                text: 'Открыть',
                onItemClick: () => editByContextMenu(event.row.data.id, event.row.data.app_ids),
            },
            {
                text: 'Копировать',
                onItemClick: () => copyByContextMenu(event.row.data.id, event.row.data.app_ids[0]),
                disabled: isDisableUiElement || isSystemRole || roleHasMoreThanOneApps,
            },
            {
                text: 'Удалить',
                onItemClick: () => deleteByContextMenu(event.row.data),
                disabled: isDisableUiElement,
            },
        );
    }
};

const copyByContextMenu = async (roleId, appId) => {
    state.mode = MODE.CREATE;
    await copyById(roleId, appId);
    state.rolePopupVisible = true;
    startWatching();
};

const editByContextMenu = async (roleId, appId) => {
    state.mode = MODE.UPDATE;
    clearRoleData();
    await loadById(roleId, appId);
    state.rolePopupVisible = true;
    startWatching();
};

const deleteByContextMenu = async rowData => {
    await deleteRoles([rowData]);
};

const deleteRoles = async rolesData => {
    const result = await dxUiConfirm(
        `Вы действительно хотите удалить ${rolesData.length > 1 ? 'выделенные роли' : 'выделенную роль'}?`,
        `Удаление ${rolesData.length > 1 ? 'ролей' : 'роли'}`,
    );
    if (!result) {
        gridInstance.value.clearSelection();
        return;
    }
    const selectedRowIds = rolesData
        .filter(row => row.code !== '-1')
        .map(row => row.id);
    await getRoleDependencies(selectedRowIds);
    const items = rolesState.roleDependenciesList;

    const rolesWithDependencies = [];
    const dependingOnUsers = [];
    items.forEach(item => {
        if (item.users.length > 0) {
            rolesWithDependencies.push(item.role_id);
            item.users.forEach(user => {
                if (!dependingOnUsers.includes(user.login)) dependingOnUsers.push(user.login);
            });
        }
    });

    const selectedSystemRole = rolesData.some(role => role.code === '-1');

    let isTheUserSure = true;

    if (selectedSystemRole) {
        const myDialogDeleteSystemRole = custom({
            title: `Удаление системной общей роли`,
            messageHtml: `<b> Системную общую роль</b> нельзя удалить`,
            width: 300,
            buttons: [
                {
                    text: 'Ок',
                    onClick: event => {
                        return { buttonText: event.component.option('text') };
                    },
                },
            ],
        });
        await myDialogDeleteSystemRole.show();
    }
    if (rolesWithDependencies.length > 0) {
        const getUsersString = arr => {
            let result = '';
            for (let i = 0; i < arr.length; i++) {
                result += arr[i];
                if (i === arr.length - 1) return result + '.\n';
                (i + 1) % 5 === 0 ? (result += '\n') : (result += ', ');
            }
            return result;
        };

        const usersString = getUsersString(dependingOnUsers);
        const rolesString = rolesData
            .filter(row => rolesWithDependencies.includes(row.id))
            .map(role => role.name)
            .join(', ');

        const getMessage = () => {
            const firstEnter = `${dependingOnUsers.length > 1 ? 'пользователям' : 'пользователю'}`;
            return rolesWithDependencies.length > 1
                ? `Внимание! Удаление невозможно, так как роли <b>${rolesString}</b> назначены ${firstEnter} <b>${usersString}`
                : `Внимание! Удаление невозможно, так как  роль <b>${rolesString}</b> назначена ${firstEnter} <b>${usersString}`;
        };

        const myDialog = custom({
            title: `Удаление ${rolesData.length > 1 ? 'ролей' : 'роли'}`,
            messageHtml: getMessage(),
            width: 500,
            buttons: [
                {
                    text: 'Ок',
                    onClick: event => {
                        return { buttonText: event.component.option('text') };
                    },
                },
            ],
        });
        await myDialog.show().then(dialogResult => {
            if (dialogResult.buttonText === 'Ок') {
                isTheUserSure = false;
            }
        });
    }

    if (!isTheUserSure || selectedSystemRole) return;

    const { success } = await deleteRole(selectedRowIds);

    if (success) {
        DxUiNotify.success(`${rolesData.length > 1 ? 'Роли удалены' : 'Роль удалена'}`);
        gridInstance.value.clearSelection();
        await refreshGridData();
    } else {
        DxUiNotify.warning(
            `${rolesData.length > 1 ? 'Ошибка при удалении, роли не удалены' : 'Ошибка при удалении, роль не удалена'}`,
        );
    }
};

const meta = {
    keyExpr: 'id',
    wordWrapEnabled: true,
    scrollingMode: 'standart',
    allowSelectAll: true,
    selectionMode: 'multiple',
    columns: [
        {
            dataField: 'id',
            caption: 'ID',
            visible: false,
        },
        {
            dataField: 'code',
            caption: 'Код',
            width: '150px',
            sortOrder: 'asc',
        },
        {
            dataField: 'name',
            caption: 'Название',
        },
        {
            dataField: 'desc',
            caption: 'Описание',
        },
        {
            dataField: 'area_names',
            caption: 'Области использования',
            allowHeaderFiltering: false,
        },
        {
            dataField: 'app_names',
            caption: 'Приложение',
        },
    ],
};
</script>

<style lang="scss" scoped>
:deep(.dx-button-text) {
    text-transform: none;
}
</style>
